'use client'

import { useState, type ReactNode } from 'react'
import { Info, WarningCircle, CheckCircle } from '@phosphor-icons/react'
import { isUsingKeyboard, useKeyboardModeTracker } from '@/lib/seasons/useKeyboardMode'

interface InputProps {
  id: string
  label: string
  type?: string
  required?: boolean
  hint?: string
  error?: string
  success?: string
  icon?: ReactNode
  iconPosition?: 'leading' | 'trailing'
  placeholder?: string
  isTextarea?: boolean
  describedBy?: string
  dark?: boolean
  disabled?: boolean
  defaultValue?: string
  value?: string
  onChange?: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void
  readOnly?: boolean
  loading?: boolean
}

export function Input({
  id,
  label,
  type = 'text',
  required = false,
  hint,
  error,
  success,
  icon,
  iconPosition = 'leading',
  placeholder,
  isTextarea = false,
  dark = false,
  disabled = false,
  defaultValue,
  value,
  onChange,
  readOnly = false,
  loading = false,
}: InputProps) {
  useKeyboardModeTracker()

  // is-focused: keyboard focus (visible focus ring)
  // is-active: filled (or mouse-focus while typing)
  const [focused, setFocused] = useState(false)
  const [active, setActive] = useState(Boolean(defaultValue || value))

  const handleFocus = () => {
    if (isUsingKeyboard()) {
      setFocused(true)
      setActive(false)
    } else {
      setFocused(false)
      setActive(true)
    }
  }
  const handleBlur = (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFocused(false)
    setActive(e.target.value.trim().length > 0)
  }

  const hintId = hint || error || success ? `hint-${id}` : undefined
  const rootClassName = [
    'input-root',
    focused && 'is-focused',
    active && !focused && 'is-active',
    error && 'is-error',
    success && 'is-success',
    disabled && 'is-disabled',
    loading && 'is-loading',
  ]
    .filter(Boolean)
    .join(' ')

  const sharedFieldProps = {
    id,
    disabled,
    readOnly,
    defaultValue,
    value,
    onChange,
    'aria-describedby': hintId,
    onFocus: handleFocus,
    onBlur: handleBlur,
  } as const

  return (
    <div className={rootClassName} data-input {...(dark ? { 'data-dark': '' } : {})}>
      <div
        className="input-container"
        onClick={(e) => {
          if (disabled) return
          const el = e.currentTarget.querySelector('input, textarea') as HTMLElement | null
          el?.focus()
        }}
      >
        <label className="input-label" htmlFor={id}>
          {label}
          {required && (
            <span className="input-label__required" aria-hidden="true">*</span>
          )}
        </label>

        {icon ? (
          <div className="input-row">
            {iconPosition === 'leading' && (
              <span className="input-icon" aria-hidden="true">{icon}</span>
            )}
            <input
              {...sharedFieldProps}
              className="input-field"
              type={type}
              required={required}
              aria-required={required || undefined}
              aria-invalid={error ? 'true' : undefined}
              placeholder={placeholder}
            />
            {iconPosition === 'trailing' && (
              <span className="input-icon" aria-hidden="true">{icon}</span>
            )}
          </div>
        ) : isTextarea ? (
          <textarea
            {...sharedFieldProps}
            className="input-textarea"
            data-is-textarea
          />
        ) : (
          <input
            {...sharedFieldProps}
            className="input-field"
            type={type}
            required={required}
            aria-required={required || undefined}
            aria-invalid={error ? 'true' : undefined}
            placeholder={placeholder}
          />
        )}
      </div>

      {error ? (
        <div className="input-help" role="alert" id={hintId}>
          <span className="input-help__icon" aria-hidden="true">
            <WarningCircle />
          </span>
          <span className="input-help__text">{error}</span>
        </div>
      ) : success ? (
        <div className="input-help input-help--success" id={hintId}>
          <span className="input-help__icon" aria-hidden="true">
            <CheckCircle />
          </span>
          <span className="input-help__text">{success}</span>
        </div>
      ) : hint ? (
        <div className="input-help input-help--hint" id={hintId}>
          <span className="input-help__icon" aria-hidden="true">
            <Info />
          </span>
          <span className="input-help__text">{hint}</span>
        </div>
      ) : null}
    </div>
  )
}
